import React from 'react';

/**
 * Global Error Boundary — App-wide safety net for render exceptions.
 *
 * Catches errors thrown during render anywhere in its subtree and shows
 * a friendly recovery UI instead of a white screen.
 *
 * Coexists with the existing AuthErrorBoundary (auth/signup routes).
 * React's nearest-boundary rule means AuthErrorBoundary catches errors
 * inside /auth and /signup first; this boundary is the safety net for
 * every other route.
 *
 * Does NOT catch:
 *   - async errors / unhandled promise rejections (those need a
 *     global window listener — out of scope this branch).
 *   - errors thrown inside event handlers (React design — wrap try/catch
 *     in the handler).
 *
 * See App Store Readiness audit 2026-02-28.
 */
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[GlobalErrorBoundary] caught render error:', error, errorInfo);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      // Last-resort: rebuild URL
      window.location.href = window.location.pathname || '/';
    }
  };

  handleSignOutAndRestart = () => {
    try {
      localStorage.clear();
    } catch { /* noop */ }
    try {
      sessionStorage.clear();
    } catch { /* noop */ }
    window.location.replace('/auth');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F7F7F5] px-6"
        data-testid="global-error-boundary"
      >
        <div className="max-w-sm w-full text-center space-y-5">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-2xl font-semibold">
            !
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
            <p className="text-sm text-gray-500">
              Celey hit an unexpected error. Reloading usually fixes it. If the
              problem continues, sign out and back in.
            </p>
          </div>
          <div className="space-y-2 pt-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:bg-emerald-700 transition-colors"
              data-testid="global-error-reload-btn"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleSignOutAndRestart}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:bg-gray-50 transition-colors"
              data-testid="global-error-signout-btn"
            >
              Sign out & restart
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default GlobalErrorBoundary;
